import { CCError } from "./errors";
import { drawCaption, findCaption } from "./render";
import { loadFont } from "./fonts";

function pickMime() {
  const opts = [
    ["video/mp4;codecs=avc1,mp4a.40.2", "mp4"],
    ["video/webm;codecs=vp9,opus", "webm"],
    ["video/webm;codecs=vp8,opus", "webm"],
    ["video/webm", "webm"],
    ["video/mp4", "mp4"],
  ];
  return opts.find(([m]) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || null;
}

export function verifyPlayable(blob) {
  return new Promise((res) => {
    const v = document.createElement("video");
    const url = URL.createObjectURL(blob);
    const done = (ok) => { clearTimeout(to); URL.revokeObjectURL(url); res(ok); };
    const to = setTimeout(() => done(false), 10000);
    v.onloadedmetadata = () => done(v.videoWidth > 0 && v.videoHeight > 0);
    v.onerror = () => done(false);
    v.muted = true;
    v.preload = "metadata";
    v.src = url;
  });
}

export async function burnVideo({ file, captions, style, onProgress, signal, mobile }) {
  const picked = pickMime();
  if (!picked || !HTMLCanvasElement.prototype.captureStream) {
    throw new CCError("UNSUPPORTED", "This browser cannot record video (MediaRecorder/captureStream missing).");
  }
  const [mime, ext] = picked;
  await loadFont(style.font);
  const url = URL.createObjectURL(file);
  const v = document.createElement("video");
  v.playsInline = true;
  v.preload = "auto";
  v.src = url;
  await new Promise((res, rej) => {
    v.onloadedmetadata = res;
    v.onerror = () => rej(new CCError("DECODE", "Your browser cannot play this video for export."));
  });
  const maxSide = mobile ? 1280 : 1920;
  const k = Math.min(1, maxSide / Math.max(v.videoWidth, v.videoHeight));
  const W = Math.round((v.videoWidth * k) / 2) * 2;
  const H = Math.round((v.videoHeight * k) / 2) * 2;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const stream = canvas.captureStream(30);
  const ac = new (window.AudioContext || window.webkitAudioContext)();
  const dest = ac.createMediaStreamDestination();
  ac.createMediaElementSource(v).connect(dest);
  dest.stream.getAudioTracks().forEach((tr) => stream.addTrack(tr));
  const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: mobile ? 4e6 : 8e6 });
  const parts = [];
  rec.ondataavailable = (e) => e.data.size && parts.push(e.data);
  const stopped = new Promise((r) => { rec.onstop = r; });

  const draw = () => {
    ctx.drawImage(v, 0, 0, W, H);
    drawCaption(ctx, W, H, findCaption(captions, v.currentTime), v.currentTime, style);
    onProgress(Math.min(1, v.currentTime / v.duration));
  };
  const loop = () => {
    if (v.ended || v.paused || signal.aborted) return;
    draw();
    if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(loop);
    else requestAnimationFrame(loop);
  };
  rec.start(1000);
  await ac.resume();
  try {
    await v.play();
  } catch (e) {
    rec.stop();
    ac.close();
    URL.revokeObjectURL(url);
    throw new CCError("UNSUPPORTED", "Playback for export was blocked: " + e.message);
  }
  loop();
  await new Promise((res) => {
    v.onended = res;
    signal.addEventListener("abort", () => { v.pause(); res(); });
  });
  draw();
  rec.stop();
  await stopped;
  ac.close();
  URL.revokeObjectURL(url);
  stream.getTracks().forEach((t) => t.stop());
  if (signal.aborted) throw new CCError("CANCELLED", "Export cancelled.");
  const blob = new Blob(parts, { type: mime.split(";")[0] });
  if (blob.size < 2048 || !(await verifyPlayable(blob))) {
    throw new CCError("INVALID", "The exported file is not a valid playable video.");
  }
  return { blob, ext, mime: mime.split(";")[0], width: W, height: H };
}
